/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-null/no-null */
import { Autocomplete, Chip, Input, ChipProps } from '@mui/material';
import { useState, forwardRef, useCallback } from 'react';

import { SearchBoxMenuOption, SearchBoxAutocompleteValue, SearchBoxOptionValue } from './search-box-types';
interface SearchBoxAutocompleteValueSegmentProps {
    option: SearchBoxMenuOption;
    onEdit: (index: number, newValue?: SearchBoxAutocompleteValue, selectedOption?: SearchBoxMenuOption) => void;
    index: number;
    value: SearchBoxOptionValue | undefined;
    className: string;
    onDelete: (event: any) => void;
    searchBoxName: string;
}

export const SearchBoxAutocompleteValueSegment = forwardRef<HTMLDivElement, SearchBoxAutocompleteValueSegmentProps>(
    ({ option, onEdit, index, className, value, searchBoxName }, ref) => {
        const [focused, setFocused] = useState(!value);
        const handleRemove = useCallback<NonNullable<ChipProps['onDelete']>>(() => {
            const focusedInput = document.querySelector(
                `.search-box-${searchBoxName} [data-input="text-segment"] input`,
            );
            if (focusedInput) {
                (focusedInput as HTMLInputElement).blur();
            }
            onEdit(index - 1);
        }, [index, onEdit, searchBoxName]);

        return focused ? (
            <Autocomplete
                isOptionEqualToValue={(opt, val) => {
                    if (opt.value === val.value) {
                        return true;
                    }
                    if (!!opt.value && !!val.value && 'object' === typeof opt.value && 'object' === typeof val.value) {
                        return (
                            (opt.value as { id?: number | string }).id === (val.value as { id?: number | string }).id
                        );
                    }

                    return false;
                }}
                onKeyDown={(event) => {
                    if ('ArrowDown' === event.key || 'ArrowUp' === event.key) {
                        event.stopPropagation();
                    }
                }}
                autoSelect
                blurOnSelect
                options={option.optionValues!}
                renderOption={(props, autocompleteOption) => {
                    if (value?.renderOption) {
                        return value.renderOption(props, autocompleteOption);
                    }

                    return <li {...props}>{autocompleteOption.label}</li>;
                }}
                getOptionLabel={(autocompleteOption) => {
                    return autocompleteOption.label;
                }}
                onInputChange={(event, inputValue, reason) => {
                    option.onInputChange?.(event, inputValue, reason);
                }}
                value={value}
                disableClearable
                openOnFocus
                onChange={(event, newValue) => {
                    onEdit(index - 1, { optionName: option.name, optionRelation: 'eq', optionValue: newValue });
                }}
                onClose={() => {
                    setFocused(false);
                }}
                // eslint-disable-next-line @typescript-eslint/naming-convention
                renderInput={({ InputProps, ...params }) => {
                    return (
                        <Input
                            {...params}
                            {...InputProps}
                            sx={{ width: 'auto' }}
                            inputProps={{
                                ...params.inputProps,
                                sx: { opacity: '1 !important', width: 'unset !important' },
                            }}
                            onKeyDown={(event) => {
                                if (
                                    ('ArrowLeft' === event.key && 0 === event.currentTarget.selectionStart) ||
                                    ('ArrowRight' === event.key &&
                                        event.currentTarget.value.length === event.currentTarget.selectionEnd) ||
                                    'ArrowDown' === event.key ||
                                    'ArrowUp' === event.key ||
                                    'Enter' === event.key ||
                                    'Tab' === event.key
                                ) {
                                    /* empty */
                                } else {
                                    event.stopPropagation();
                                }
                            }}
                            autoFocus
                        />
                    );
                }}
            />
        ) : (
            <Chip
                {...{ 'data-tag-index': index }}
                ref={ref}
                tabIndex={-1}
                className={className}
                label={value?.label ?? ''}
                sx={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }}
                size='medium'
                onFocus={() => {
                    setFocused(true);
                }}
                onClick={() => {
                    setFocused(true);
                }}
                onDelete={handleRemove}
            />
        );
    },
);
